import { ToastrService } from 'ngx-toastr';
import { Meta, Title } from '@angular/platform-browser';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { MatOptionSelectionChange } from '@angular/material/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { catchError, combineLatest, take, of, takeUntil, Subject } from 'rxjs';
import { difference, findIndex, flatMap, isEqual, map, without, sumBy, forEach, reduce, includes } from 'lodash';
import { Component, OnInit, Input, ChangeDetectorRef, OnDestroy, Renderer2, ElementRef, HostListener, Inject, PLATFORM_ID } from '@angular/core';

import { ByMarkRatingType, ContentJsonType } from '@sifca-monorepo/ecommerce-generator';
import { AverageRatingType } from '@sifca-monorepo/ecommerce-generator';

import { AppService, AuthService, SeoService, SharedService } from '../../../services';
import { OrdersService } from '../../../services/order/order/order.service';

declare const FB: any;

@Component({
  template: '',
})
export class BaseProductDetailComponent implements OnInit, OnDestroy {
  private unsubscribeAll: Subject<any> = new Subject<any>();
  @Input() product: any;

  private tags: HTMLMetaElement[];

  qty = 1;
  count = 0;
  items: any[];
  links: any[] = [];
  posId: string = '';
  overAllAverage = 0;
  scripts: any[] = [];
  seo: ContentJsonType;
  isOutOfStock: boolean;
  productForm: FormGroup;
  internalProductId: any;
  updatedAttributes: any;
  authenticated: boolean;
  discountedPrice: number;
  selectedBarcodeIndex = 0;
  isButtonDisabled: boolean;
  attributeControl: FormGroup;
  ratingStats: ByMarkRatingType[];
  averageRating: AverageRatingType;
  selectedAttributeValues: string[] = [];

  get pictures() {
    return ((this.productForm?.get('barcode') as FormGroup)?.get('media') as FormGroup)?.get('pictures') as FormArray;
  }
  get price() {
    return (this.productForm?.get('barcode') as FormGroup)?.get('price') as FormGroup;
  }
  get publicPriceBeforeReduction() {
    return (this.productForm?.get('barcode') as FormGroup)?.get('publicPriceBeforeReduction') as FormGroup;
  }
  get discount() {
    return (this.productForm?.get('barcode') as FormGroup)?.get('discount') as FormGroup;
  }
  get name() {
    return (this.productForm?.get('barcode') as FormGroup)?.get('name') as FormGroup;
  }
  get currentBarcodeId() {
    return (this.productForm?.get('barcode') as FormGroup)?.get('id') as FormGroup;
  }

  constructor(
    protected el: ElementRef,
    protected renderer: Renderer2,
    protected metaTagService: Meta,
    protected appService: AppService,
    protected seoService: SeoService,
    protected titleTagService: Title,
    protected authService: AuthService,
    protected formBuilder: FormBuilder,
    protected orderService: OrdersService,
    protected toastrService: ToastrService,
    protected sharedService: SharedService,
    protected changeDetectorRef: ChangeDetectorRef,
    @Inject(DOCUMENT) protected document: Document,
    @Inject(PLATFORM_ID) protected platformId: Object,
  ) {
    this.sharedService.posId$.subscribe((posId) => {
      this.posId = posId;
    });
    this.authService.authenticated$.subscribe((authenticated: boolean) => {
      this.authenticated = authenticated;
      this.changeDetectorRef.markForCheck();
    });
  }

  ngOnInit(): void {
    this.appService.searchString = '';
    if (!this.authenticated) {
      this.appService.loadCart();
      this.appService.items$.pipe(takeUntil(this.unsubscribeAll)).subscribe((items: any[]) => {
        this.items = items;
        this.changeDetectorRef.markForCheck();
      });
    }
    this.appService.product$.pipe(takeUntil(this.unsubscribeAll)).subscribe((product: any) => {
      if (product) {
        this.product = product;
        if (!this.product.barcodes?.length) {
          this.isButtonDisabled = true;
        }
        this.internalProductId = product.id;
        this.getSimpleProductSEOJSON();
        this.selectedAttributeValues =
          this.product.barcodes && this.product.barcodes.length > 0
            ? map(this.product.barcodes[0].productAttributesValues?.attributesValues, (values) => values.id)
            : [];
        this.changeDetectorRef.markForCheck();
        this.updatedAttributes = product.barcodes
          .flatMap((barcode) => barcode.productAttributesValues?.attributesValues)
          .reduce((acc, current) => {
            const index = acc.findIndex((item) => item.attribute.label === current?.attribute?.label);
            if (index === -1) {
              acc.push({
                attribute: {
                  label: current?.attribute?.label,
                  isRequired: current?.attribute?.isRequired,
                  isMultipleChoice: current?.attribute?.isMultipleChoice,
                },
                attributeValues: [
                  {
                    label: current?.label,
                    id: current?.id,
                  },
                ],
              });
            } else {
              const attributeValueIndex = acc[index].attributeValues.findIndex((item) => item?.label === current?.label);
              if (attributeValueIndex === -1) {
                acc[index].attributeValues.push({
                  label: current?.label,
                  id: current?.id,
                });
              }
            }
            return acc;
          }, []);
        if (this.product.barcodes?.length) {
          this.productForm = this.formBuilder.group({
            barcode: this.formBuilder.group({
              id: [product.barcodes[0].id || ''],
              media: this.formBuilder.group({
                pictures: this.formBuilder.array(
                  map(product.barcodes[0].media?.pictures, (picture) => {
                    return this.formBuilder.group({ path: picture.path, baseUrl: picture.baseUrl });
                  }),
                ),
              }),
              price: [product.barcodes[0]?.publicPrice || '0'],
              publicPriceBeforeReduction: [product.barcodes[0]?.publicPriceBeforeReduction || '0'],
              name: [product.barcodes[0]?.name || ''],
              discount: this.formBuilder.group({
                amount: [product.barcodes[0]?.discount?.amount],
                discountType: [product.barcodes[0]?.discount?.discountType],
              }),
            }),
            quantity: [1],
            attributes: this.formBuilder.array(
              flatMap(product?.barcodes, (barcode) =>
                map(barcode.productAttributesValues?.attributesValues, (attributeValue) => {
                  return this.formBuilder.group({
                    label: attributeValue?.label,
                    id: [attributeValue.id, [Validators.required, Validators.min(1)]],
                    price: barcode?.publicPrice,
                  });
                }),
              ),
            ),
          });
          this.getStockByTargetAndBarcode(product.barcodes[0].id);
        }
        this.getAverageRatingwithStats();
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  getAverageRatingwithStats() {
    combineLatest([
      this.appService.getCorporateRatingsByProductWithStats(this.internalProductId),
      this.appService.getAverageCorporateProductRatingsByProduct(this.internalProductId),
    ])
      .pipe(
        catchError(() => {
          return of(null);
        }),
      )
      .subscribe((res) => {
        if (res?.length) {
          if (res[0]?.stats) {
            this.ratingStats = res[0]?.stats;
            this.count = sumBy(this.ratingStats, 'count');
          }
          if (res[1]) {
            this.averageRating = res[1];
            this.overAllAverage = Math.round(Number(res[1].overAllAverage));
          }
        }
        this.changeDetectorRef.markForCheck();
      });
  }

  checkAttributes(change: MatOptionSelectionChange, field: string, attributeValues: any[]): void {
    if (change.isUserInput) {
      if (field !== 'multiple') {
        this.selectedAttributeValues = [
          ...reduce(
            this.selectedAttributeValues,
            (accumulator: any, current) => {
              if (includes(map(attributeValues, 'id'), current)) {
                return accumulator;
              }
              return [...accumulator, current];
            },
            [],
          ),
          change.source.value,
        ];
      } else {
        if (change?.source?.selected) {
          this.selectedAttributeValues.push(change.source.value);
        } else {
          this.selectedAttributeValues = without(this.selectedAttributeValues, change.source.value);
        }
      }
      this.changeDetectorRef.markForCheck();
      this.getBarcode();
    }
  }

  getSimpleProductSEOJSON(index = 0) {
    if (this.product.barcodes.length) {
      this.seoService
        .getSimpleProductSEOJSON(this.posId, this.product.barcodes[index].id)
        .pipe(take(1))
        .subscribe((res) => {
          if (res) {
            this.seo = res;
            if (this.seo?.title) {
              this.titleTagService.setTitle(this.seo?.title);
            }
            this.tags = this.metaTagService.addTags([
              ...map(this.seo?.metas, (meta) => ({
                ...(meta.name ? { name: meta.name } : {}),
                ...(meta.property ? { property: meta.property } : {}),
                content: meta.content,
              })),
              { charset: 'UTF-8' },
            ]);
              forEach(this.seo?.scripts, (seoScript) => {
                const script = this.renderer.createElement('script');
                if (seoScript.type) {
                  script.type = seoScript.type;
                }
                if (seoScript.content) {
                  script.text = seoScript.content;
                }
                this.scripts = [...this.scripts, script];
                this.renderer.appendChild(this.document.head, script);
              });
              forEach(this.seo?.links, (seoLink) => {
                const link = this.renderer.createElement('link');
                if (seoLink.sizes) {
                  link.sizes = seoLink.sizes;
                }
                if (seoLink.href) {
                  link.href = seoLink.href;
                }
                if (seoLink.rel) {
                  link.rel = seoLink.rel;
                }
                this.links = [...this.links, link];
                this.renderer.appendChild(this.document.head, link);
              });
          }
        });
    }
  }

  getBarcode() {
    this.selectedBarcodeIndex = findIndex(this.product.barcodes, (barcode: any) => {
      return difference(this.selectedAttributeValues, map(barcode.productAttributesValues?.attributesValues, 'id')).length === 0;
    });
    if (
      this.selectedBarcodeIndex > -1 &&
      isEqual(
        this.selectedAttributeValues.sort(),
        map(this.product.barcodes[this.selectedBarcodeIndex].productAttributesValues?.attributesValues, 'id').sort(),
      )
    ) {
      forEach(this.links, (link) => this.renderer.removeChild(this.document.head, link));
      forEach(this.tags, (tag) => this.metaTagService.removeTagElement(tag));
      this.appService.barcodeIndex$ = this.selectedBarcodeIndex;
      (this.productForm.get('barcode') as FormGroup).patchValue({
        id: this.product.barcodes[this.selectedBarcodeIndex]?.id || '',
        name: this.product.barcodes[this.selectedBarcodeIndex]?.name || '',
        price: this.product.barcodes[this.selectedBarcodeIndex]?.publicPrice,
        publicPriceBeforeReduction: this.product.barcodes[this.selectedBarcodeIndex].publicPriceBeforeReduction,
        discount: {
          amount: this.product.barcodes[this.selectedBarcodeIndex]?.discount?.amount,
          discountType: this.product.barcodes[this.selectedBarcodeIndex]?.discount?.discountType,
        },
      });
      this.getSimpleProductSEOJSON(this.selectedBarcodeIndex);
      this.getStockByTargetAndBarcode(this.product.barcodes[this.selectedBarcodeIndex]?.id);
    } else {
      this.appService.barcodeIndex$ = -1;
      this.productForm.reset();
      this.isOutOfStock = true;
      this.changeDetectorRef.markForCheck();
    }
  }

  getStockByTargetAndBarcode(barcodeId: string) {
    this.appService
      .getStockByTargetAndBarcode(barcodeId)
      .pipe(
        catchError(() => {
          this.isOutOfStock = true;
          return of(null);
        }),
      )
      .subscribe((res) => {
        if (res) {
          this.isOutOfStock = res.outOfStock;
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  addToCart() {
    if (!this.authenticated) {
      const index = this.product.barcodes.findIndex((barcode) => barcode.id === this.currentBarcodeId.value);
      if (index > -1 && this.appService.itemInCart(this.product.barcodes[index])) {
        const value: any = {
          barcode: this.product.barcodes[index],
          price: {
            unitGross: this.product.barcodes[index]?.publicPrice,
            gross: this.product.barcodes[index]?.publicPrice,
          },
          product: this.product,
          quantity: this.productForm.get('quantity').value,
        };
        this.appService.updateLocalCart(value);
        this.toastrService.success('Produit ajouté au panier.');
      } else {
        const value: any = {
          barcode: this.product.barcodes[index],
          price: {
            unitGross: this.product.barcodes[index]?.publicPrice,
            gross: this.product.barcodes[index]?.publicPrice,
          },
          product: this.product,
          quantity: this.productForm.get('quantity').value,
        };
        this.appService.addToLocalCart(value);
        this.toastrService.success('Produit ajouté au panier.');
      }
      this.appService.loadCart();
    } else {
      this.isButtonDisabled = true;
      const formValue: any = {
        barcode: this.currentBarcodeId.value,
        quantity: this.qty,
      };
      this.orderService
        .addProductsToCart([formValue])
        .pipe(
          catchError(() => {
            this.toastrService.error("Quelque chose s'est mal passé");
            this.changeDetectorRef.markForCheck();
            return of(null);
          }),
        )
        .subscribe((res) => {
          if (res) {
            this.isButtonDisabled = false;
            this.toastrService.success('Produit ajouté au panier.');
            this.changeDetectorRef.markForCheck();
          }
        });
    }
  }

  shareOnFacebook() {
    const shareUrl = `https://cocospacio.com/p/${this.product?.seo?.urlKey}`;
    FB.init({
      appId: '278013208459559',
      autoLogAppEvents: true,
      xfbml: true,
      version: 'v22.0',
    });
    (window as any).FB.ui(
      {
        display: 'popup',
        method: 'share_open_graph',
        action_type: 'og.likes',
        action_properties: JSON.stringify({
          object: shareUrl,
        }),
      },
      function (response) {
        if (response && !response.error_message) {
          // The user successfully shared the content
          console.log('Shared successfully');
          // Perform additional actions if needed
        } else {
          // There was an error or the user canceled the share
          console.error('Share error:', response);
          // Perform additional error handling if needed
        }
      },
    );
  }

  sendOnFacebook() {
    const shareUrl = `https://cocospacio.com/p/${this.product?.seo?.urlKey}`;
    FB.ui(
      {
        method: 'send',
        link: shareUrl,
      },
      function (response) {
        if (response && !response.error_message) {
          // The user successfully shared the content
          console.log('Shared successfully');
          // Perform additional actions if needed
        } else {
          // There was an error or the user canceled the share
          console.error('Share error:', response);
          // Perform additional error handling if needed
        }
      },
    );
  }

  loginFacebook() {
    FB.login(
      function (loginResponse) {
        if (loginResponse.authResponse) {
          // User is logged in and authenticated
          console.log('User logged in:', loginResponse.authResponse);

          // Call FB.ui to trigger the share dialog
          FB.ui(
            {
              method: 'share',
              href: 'https://cocospacio.com', // Specify the URL you want to share
            },
            function (shareResponse) {
              console.log('shareResponse ', shareResponse);
              // Handle response from the share dialog
              if (shareResponse && !shareResponse.error_message) {
                // The user successfully shared the content
                console.log('Shared successfully');
                // Perform additional actions if needed
              } else {
                // There was an error or the user canceled the share
                console.error('Share error:', shareResponse);
                // Perform additional error handling if needed
              }
            },
          );
        } else {
          // User canceled the login or an error occurred
          console.log('Login error:', loginResponse.status);
        }
      },
      { scope: 'email' },
    );
  }

  checkFacebookLoginStatus() {
    FB.getLoginStatus(function (response) {
      if (response.status === 'connected') {
        // User is logged in and authenticated with your app
        const accessToken = response.authResponse.accessToken;
        console.log('User is logged in:', accessToken);
      } else if (response.status === 'not_authorized') {
        // User is logged in to Facebook but has not authenticated your app
        console.log('User is logged in but not authorized with your app');
      } else {
        // User is not logged in to Facebook
        console.log('User is not logged in to Facebook');
      }
    });
  }

  shareOnTikTok() {
    console.log('url: ', `${window.location.href}/p/${this.product?.seo?.urlKey}`);
    if (isPlatformBrowser(this.platformId)) {
      if (navigator.share) {
        navigator
          .share({
            title: 'Share to TikTok',
            text: 'content',
            url: `${window.location.href}/p/${this.product?.seo?.urlKey}`,
          })
          .then(() => {
            console.log('Shared successfully to TikTok');
            // You can trigger any additional actions here after successful sharing
          })
          .catch((error) => {
            console.error('Error sharing to TikTok:', error);
            // Handle errors here
          });
      } else {
        // Fallback for browsers that don't support Web Share API
        console.error('Web Share API is not supported');
        // Implement alternative sharing method or inform the user that sharing is not supported
      }
    }
  }

  shareOnTwitter(hashtags?: string[], callback?: (success: boolean) => void) {
    let shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent('text')}&url=${encodeURIComponent(
      `${window.location.href}/p/${this.product?.seo?.urlKey}`,
    )}`;
    if (hashtags && hashtags.length > 0) {
      shareUrl += `&hashtags=${encodeURIComponent(hashtags.join(','))}`;
    }

    const popupWidth = 600;
    const popupHeight = 400;
    const popupLeft = window.innerWidth / 2 - popupWidth / 2;
    const popupTop = window.innerHeight / 2 - popupHeight / 2;
    const popup = window.open(shareUrl, 'twitter-share-dialog', `width=${popupWidth},height=${popupHeight},left=${popupLeft},top=${popupTop}`);

    if (popup) {
      const pollTimer = setInterval(() => {
        if (popup.closed !== false) {
          clearInterval(pollTimer);
          if (callback) {
            if (document.body.textContent.includes('Your post was sent.')) {
              console.log('Twitter share successful');
              callback(true); // Sharing completed successfully
            } else {
              callback(false); // Sharing failed
            }
          }
        }
      }, 200);
    } else {
      // Popup blocked, open sharing URL in new tab
      window.open(shareUrl, '_blank');
      if (callback) {
        callback(false);
      }
    }
  }

  onChangeQty(current: number) {
    this.qty = current;
    this.productForm.get('quantity').patchValue(this.qty);
  }

  @HostListener('window:scroll', ['$event'])
  handleScroll(event: Event) {
    if (this.document.querySelector('.default-page')) {
      this.scrollHandler();
    }
  }

  scrollHandler() {
    let stickyBar = this.el.nativeElement.querySelector('.sticky-bar');
    if (stickyBar.classList.contains('d-none') && this.el.nativeElement.getBoundingClientRect().bottom < 0) {
      stickyBar.classList.remove('d-none');
      return;
    }
    if (!stickyBar.classList.contains('d-none') && this.el.nativeElement.getBoundingClientRect().bottom > 0) {
      stickyBar.classList.add('d-none');
    }
  }

  ngOnDestroy() {
    forEach(this.links, (link) => {
      return this.renderer.removeChild(this.document.head, link);
    });
    forEach(this.tags, (tag) => this.metaTagService.removeTagElement(tag));
    this.appService.product$ = null;
    this.product = null;
    this.productForm = null;
    this.appService.corporateRating$ = null;
    this.appService.filtredProducts$ = null;
  }
}
